/**
 * Per-Tile LOD Management Hook
 * Manages individual tile quality levels based on camera frustum visibility
 */

import { useRef, useEffect, useState, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { getVisibleTiles } from '../utilities/frustumCulling'
import lodConfig from '../config/lodConfig.json'
import { getDeviceInfo } from '../../../utils/deviceDetection'

/**
 * Hook to manage per-tile LOD levels based on camera visibility
 * @param {Object} tileGrid - Grid configuration {columns, rows, totalTiles}
 * @param {number} sphereRadius - Radius of the Earth sphere
 * @param {boolean} enabled - Whether per-tile LOD is enabled
 * @returns {Object} { tileQualities, visibleTiles }
 */
export function usePerTileLOD(tileGrid, sphereRadius = 2.5, enabled = true) {
  const { camera } = useThree()
  
  // Detect device and orientation (memoized)
  const deviceInfo = useMemo(() => getDeviceInfo(), [])
  const isMobile = deviceInfo.isMobile
  const lodLevels = useMemo(() => isMobile ? lodConfig.mobile.lodLevels : lodConfig.desktop.lodLevels, [isMobile])
  
  // Track quality level for each tile (index -> LOD level name)
  const [tileQualities, setTileQualities] = useState(() => {
    // Initialize all tiles to lowest quality
    const qualities = new Array(tileGrid.totalTiles).fill('LOD_3')
    return qualities
  })

  const [visibleTiles, setVisibleTiles] = useState(new Set())
  
  // Throttle updates to avoid excessive recalculation
  const lastUpdateTime = useRef(0)
  const updateInterval = 100 // ms

  useFrame((state) => {
    if (!enabled) return

    const now = state.clock.getElapsedTime() * 1000
    if (now - lastUpdateTime.current < updateInterval) return
    lastUpdateTime.current = now

    // Calculate which tiles are visible
    const visible = getVisibleTiles(camera, tileGrid, sphereRadius)
    
    // Only update if visibleTiles changed - avoid array creation
    if (visible.size !== visibleTiles.size) {
      setVisibleTiles(visible)
    } else {
      // Check if content changed without creating array
      let hasChanges = false
      for (const tile of visible) {
        if (!visibleTiles.has(tile)) {
          hasChanges = true
          break
        }
      }
      if (hasChanges) {
        setVisibleTiles(visible)
      }
    }

    // Calculate camera distance from origin for LOD selection
    const distance = camera.position.length()

    // Determine target quality based on distance
    let targetQuality = 'LOD_3'
    for (const lod of lodLevels) {
      if (distance >= lod.minDistance && distance < lod.maxDistance) {
        targetQuality = lod.name
        break
      }
    }

    // Update tile qualities
    setTileQualities((prevQualities) => {
      let hasChanges = false
      let newQualities = null // Only create if needed

      for (let i = 0; i < tileGrid.totalTiles; i++) {
        const shouldBeHighRes = visible.has(i)
        const currentQuality = prevQualities[i]
        const desiredQuality = shouldBeHighRes ? targetQuality : 'LOD_3'

        if (currentQuality !== desiredQuality) {
          if (!newQualities) {
            newQualities = [...prevQualities] // Create only when changes are needed
          }
          newQualities[i] = desiredQuality
          hasChanges = true
        }
      }

      return hasChanges ? newQualities : prevQualities
    })
  })

  return {
    tileQualities,
    visibleTiles,
    visibleCount: visibleTiles.size
  }
}
